// =====================================================================
// WeddingOS – src/pages/guest/JoinLookup.jsx
// "Bereits eingeladen?" Screen
// =====================================================================
// Route: /join  (ohne Token)
// Zeigt zwei Optionen:
//   1. Einladungscode direkt eingeben → /join/:token
//   2. E-Mail eingeben → Link wird erneut gesendet
// =====================================================================

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import "../../styles/theme.css";

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY;

export default function JoinLookup() {
  const navigate = useNavigate();
  const [tab,     setTab]     = useState("email"); // "email" | "token"
  const [email,   setEmail]   = useState("");
  const [token,   setToken]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState(null);
  const [found,   setFound]   = useState(null); // { wedding_name, guest_name, table_token }

  // ── E-Mail Lookup ─────────────────────────────────────────────────
  const handleEmailSubmit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Gast per E-Mail finden
      const { data, error: e } = await supabase.rpc("find_guest_by_email", {
        p_email: email.trim().toLowerCase(),
      });
      const result = Array.isArray(data) ? data[0] : data;

      if (e || !result) {
        setError("Keine Einladung für diese E-Mail-Adresse gefunden. Bitte das Brautpaar kontaktieren.");
        return;
      }

      setFound(result);

      // 2. Einladungs-E-Mail erneut senden
      await fetch(`${SUPABASE_URL}/functions/v1/resend-invitation`, {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      setSent(true);
    } catch (err) {
      setError("Etwas ist schiefgegangen. Bitte nochmal versuchen.");
    } finally {
      setLoading(false);
    }
  };

  // ── Token direkt eingeben ─────────────────────────────────────────
  const handleTokenSubmit = () => {
    const t = token.trim();
    if (!t) return;
    // Token aus vollem Link extrahieren falls jemand den Link einkopiert
    const match = t.match(/\/join\/([a-zA-Z0-9_-]+)$/);
    navigate(`/join/${match ? match[1] : t}`);
  };

  return (
    <div style={S.page}>
      {/* Logo */}
      <div style={S.logo}>
        <div style={S.logoIcon}>✦</div>
        <span style={S.logoText}>unsereHochzeits<span style={{ color: "var(--accent)" }}>.app</span></span>
      </div>

      <h1 style={S.title}>Bereits eingeladen?</h1>
      <p style={S.sub}>
        Gib deine E-Mail-Adresse ein – wir schicken dir deinen persönlichen Zugangslink erneut.
      </p>

      {/* Tabs */}
      <div style={S.tabs}>
        <button
          onClick={() => { setTab("email"); setError(null); setSent(false); setFound(null); }}
          style={{ ...S.tab, ...(tab === "email" ? S.tabActive : {}) }}
        >
          Per E-Mail
        </button>
        <button
          onClick={() => { setTab("token"); setError(null); }}
          style={{ ...S.tab, ...(tab === "token" ? S.tabActive : {}) }}
        >
          Code eingeben
        </button>
      </div>

      {/* ── E-Mail Tab ── */}
      {tab === "email" && !sent && (
        <div style={S.form}>
          <label style={S.label}>Deine E-Mail-Adresse</label>
          <input
            type="email"
            className="wos-input"
            placeholder="deine@email.at"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(null); }}
            onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
            style={{ width: "100%", marginTop: 6 }}
            autoFocus
          />
          {error && <p style={S.error}>{error}</p>}
          <button
            className="wos-btn wos-btn-primary"
            onClick={handleEmailSubmit}
            disabled={loading || !email.trim()}
            style={{ width: "100%", marginTop: 16 }}
          >
            {loading ? "Suche …" : "Link zusenden →"}
          </button>
        </div>
      )}

      {/* ── E-Mail gesendet ── */}
      {tab === "email" && sent && found && (
        <div style={S.successBox}>
          <span style={{ fontSize: 28, display: "block", marginBottom: 12 }}>✉</span>
          <p style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", margin: "0 0 8px" }}>
            Link gesendet!
          </p>
          <p style={{ fontSize: 14, color: "var(--ink-muted)", lineHeight: 1.6, margin: "0 0 16px" }}>
            Wir haben den Zugangslink für <strong>{found.wedding_name}</strong> an <strong>{email}</strong> gesendet.
          </p>
          <p style={{ fontSize: 13, color: "var(--ink-subtle)" }}>
            Schau in deinen Posteingang – der Link ist sofort gültig.
          </p>
          {/* Direkt öffnen als Fallback */}
          {found.table_token && (
            <button
              className="wos-btn wos-btn-ghost"
              onClick={() => navigate(`/join/${found.table_token}`)}
              style={{ width: "100%", marginTop: 16 }}
            >
              Direkt öffnen →
            </button>
          )}
        </div>
      )}

      {/* ── Token Tab ── */}
      {tab === "token" && (
        <div style={S.form}>
          <label style={S.label}>Einladungslink oder -code</label>
          <input
            type="text"
            className="wos-input"
            placeholder="https://unserehochzeits.app/join/… oder Code"
            value={token}
            onChange={e => { setToken(e.target.value); setError(null); }}
            onKeyDown={e => e.key === "Enter" && handleTokenSubmit()}
            style={{ width: "100%", marginTop: 6 }}
            autoFocus
          />
          <p style={{ fontSize: 11, color: "var(--ink-subtle)", marginTop: 6 }}>
            Du findest deinen Link in der Einladungs-E-Mail oder auf deiner Tischkarte.
          </p>
          {error && <p style={S.error}>{error}</p>}
          <button
            className="wos-btn wos-btn-primary"
            onClick={handleTokenSubmit}
            disabled={!token.trim()}
            style={{ width: "100%", marginTop: 16 }}
          >
            Öffnen →
          </button>
        </div>
      )}

      {/* Hilfe */}
      <p style={S.help}>
        Keinen Link erhalten?{" "}
        <span style={{ color: "var(--accent)" }}>
          Bitte das Brautpaar um deinen persönlichen Einladungslink.
        </span>
      </p>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight:   "100dvh",
    background:  "var(--bg)",
    fontFamily:  "var(--font-body)",
    color:       "var(--ink)",
    maxWidth:    420,
    margin:      "0 auto",
    padding:     "48px 24px 60px",
    boxSizing:   "border-box",
  },
  logo: {
    display:       "flex",
    alignItems:    "center",
    gap:           "0.6rem",
    marginBottom:  32,
  },
  logoIcon: {
    width:          32,
    height:         32,
    border:         "1px solid rgba(201,149,108,0.5)",
    borderRadius:   8,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    fontSize:       "0.8rem",
    color:          "var(--accent)",
  },
  logoText: {
    fontFamily: "var(--font-display)",
    fontSize:   "1.5rem",
    fontWeight: 600,
    color:      "var(--ink)",
  },
  title: {
    fontFamily: "var(--font-display)",
    fontSize:   "clamp(1.8rem, 6vw, 2.2rem)",
    fontWeight: 500,
    lineHeight: 1.1,
    margin:     "0 0 12px",
  },
  sub: {
    fontSize:   14,
    color:      "var(--ink-muted)",
    lineHeight: 1.7,
    margin:     "0 0 28px",
  },
  tabs: {
    display:      "flex",
    gap:          8,
    marginBottom: 24,
  },
  tab: {
    flex:         1,
    padding:      "9px 0",
    borderRadius: "999px",
    border:       "1px solid var(--line)",
    background:   "var(--surface)",
    color:        "var(--ink-muted)",
    fontSize:     13,
    cursor:       "pointer",
    fontFamily:   "var(--font-body)",
    transition:   "all 0.15s",
  },
  tabActive: {
    background: "var(--accent)",
    color:      "#fff",
    border:     "1px solid var(--accent)",
    fontWeight: 600,
  },
  form: { display: "flex", flexDirection: "column" },
  label: {
    fontSize:   13,
    fontWeight: 500,
    color:      "var(--ink)",
  },
  error: {
    fontSize:  13,
    color:     "var(--error, #C0392B)",
    marginTop: 8,
    margin:    "8px 0 0",
  },
  successBox: {
    textAlign:    "center",
    background:   "var(--forest-soft)",
    border:       "1px solid var(--forest)",
    borderRadius: "var(--radius-md)",
    padding:      "28px 24px",
  },
  help: {
    fontSize:  12,
    color:     "var(--ink-subtle)",
    marginTop: 32,
    textAlign: "center",
    lineHeight: 1.6,
  },
};
